/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/ban-types */

import * as Thunk from "redux-thunk";

import { State } from "~/State";
import { Mutable } from "~/Mutable";

/**
 * `Action.Definition`s provide an easy way to create type-safe Redux actions
 * without mountains of boilerplate. The general idea is to compose a
 * `Definition` using helper functions which can then be exported as a set of
 * standard action exports, i.e. `Action`, `TYPE`, `create`, and `reduce`.
 */
export type Definition<Config extends Config.Config> =
  | WithReducer<Config>
  | WithoutReducer<Config>;

type WithReducer<Config extends Config.Config> = TypeAndCreate<Config> & {
  readonly reduce: Reducer<Config>;
};

type WithoutReducer<Config extends Config.Config> = TypeAndCreate<Config> & {
  readonly reduce?: undefined;
};

/**
 * Every `Definition` must have a unique `TYPE` (which is a standard Redux
 * action type string constant) and a function `create`, which creates the
 * action
 */
type TypeAndCreate<Config extends Config.Config> = {
  readonly TYPE: Config["TYPE"];
  readonly create: Creator<Config>;
};

/** A `Creator` has many or no `args` and returns an `Action` */
type Creator<Config extends Config.Config> = (
  ...args: Config["args"]
) => Config.ToAction<Config>;

/** A standard Redux `Reducer` */
type Reducer<Config extends Config.Config> = (
  state: State,
  action: Config.ToAction<Config>
) => State;

/** One or many `Effect`s to run after an action is created */
type Effects<Config extends Config.Config> = readonly Effect<Config>[] & {
  readonly 0: Effect<Config>;
};

/**
 * An `Effect` is any side-effect which runs after an action is created.
 * `Effect`s can dispatch new action and read from `State` and the original
 * `Action`.
 */
type Effect<Config extends Config.Config> = (args: {
  readonly dispatch: Dispatch;
  readonly action: Config.ToAction<Config>;
  readonly state: State;
}) => void;

/** Standard Redux thunk action */
type Thunk<Config extends Config.Config> = Thunk.ThunkAction<
  Config.ToAction<Config>,
  State,
  undefined,
  { readonly type: string }
>;

/** Standard Redux dispatch function available to `Effect`s */
type Dispatch = Thunk.ThunkDispatch<
  State,
  undefined,
  { readonly type: string }
>;

namespace Config {
  /**
   * `Definition.Config` is used by a `Definition` to derive exactly what gets
   * exported
   */
  export type Config = {
    /**
     * What are the `args` for the `action` function? There can be none, or a
     * variable number of `any` type. Importantly, the `args` for the `action`
     * function are strongly typed, e.g. `readonly any[]` becomes
     * `companyID: string`.
     */
    readonly args: readonly any[];

    /**
     * Every Redux action must have a unique `TYPE`, which must be given as
     * `"Some action type" as const` so the compiler knows it's a specific
     * string
     */
    readonly TYPE: string;

    /** Most Redux actions have a `payload` in addition to the `type` field */
    readonly payload: object;
  };

  /**
   * Convert a `Config` into an actual action type...
   *
   * ```ts
   * type Action = ToAction<{
   *   args: [],
   *   TYPE: "The type",
   *   payload: {
   *     someString: string,
   *     aNumber: number
   *   }
   * }>
   *
   * const action: Action = { type: "The type", someString: "a", aNumber: 123 }
   * ```
   */
  export type ToAction<Config extends Config.Config> = {
    readonly type: Config["TYPE"];
  } & Config["payload"];
}

/**
 * Given a function which is probably a `Creator`, extract its resulting
 * `Action` type...
 *
 * ```ts
 * type Action = FromCreator<() => { type: "The type", test: true }>
 *
 * const action: Action = { type: "The type", test: true }
 * ```
 *
 * This is really useful when combined with an exported `create` function...
 *
 * ```ts
 * export type Action = FromCreator<typeof action>
 *
 * export const { TYPE, create, reduce } = "Some definition..."
 * ```
 */
export type FromCreator<Creator extends AnyFunction> = ReturnType<
  Creator
> extends ThunkAction<infer Action> | { readonly type: string }
  ? Action | Exclude<ReturnType<Creator>, ThunkAction<any>>
  : ReturnType<Creator> extends ThunkAction<infer Action>
  ? Action
  : ReturnType<Creator>;

type AnyFunction = (...args: readonly any[]) => any;
type ThunkAction<Action> = Thunk.ThunkAction<Action, any, any, any>;

/**
 * Define a Redux action with a unique `TYPE` constant...
 *
 * ```ts
 * export const { TYPE, action } = type("The type" as const)
 * // ^ `as const` is required for the compiler to respect the literal string
 *
 * action() // `{ type: "The type" }`
 * ```
 */
export const type = <
  Type extends string,
  Config extends {
    readonly args: readonly [];
    readonly TYPE: Type;
    readonly payload: {};
  }
>(
  TYPE: Type
): WithoutReducer<Config> => ({ TYPE, create: () => ({ type: TYPE }) });

/**
 * Define a Redux action `creator` given an existing `Definition`...
 *
 * ```ts
 * export const { TYPE, action } = pipe(
 *   type("Message sent" as const),
 *   creator((message: string) => ({ value: message })) // Must return an object
 * )
 *
 * action("Hey!") // `{ type: "Message sent", value: "Hey!" }`
 * ```
 *
 * Note how the object returned from `creator` becomes the `payload`.
 */
export const creator = <
  Args extends readonly any[],
  Payload extends object,
  /**
   * The previous `Definition` cannot have a creator defined, i.e. its
   * `action` must have no arguments and return an empty `payload`
   */
  PreviousConfig extends {
    readonly TYPE: string;
    readonly args: readonly [];
    readonly payload: {};
  },
  UpdatedConfig extends {
    readonly TYPE: PreviousConfig["TYPE"];
    readonly args: Args;
    readonly payload: Payload;
  }
>(
  creator: (...args: Args) => Payload
) => (
  definition: WithoutReducer<PreviousConfig>
): WithoutReducer<UpdatedConfig> => ({
  ...definition,
  create: (...args) => ({ type: definition.TYPE, ...creator(...args) }),
});

/**
 * Define a Redux action `reducer` given an existing `Definition`...
 *
 * ```ts
 * export const { TYPE, create, reduce } = pipe(
 *   type("Toggle switch" as const),
 *   creator((toggle: boolean) => ({ toggle })),
 *   reducer(({ state, action: { toggle } }) => (state.switch = toggle))
 * )
 * ```
 */
export const reducer = <Config extends Config.Config>(
  update: (args: {
    readonly state: State.Next;
    readonly action: Config.ToAction<Config>;
  }) => void
) => (definition: WithoutReducer<Config>): WithReducer<Config> => ({
  ...definition,
  reduce: (state, action) =>
    Mutable.update(state, (next) => void update({ state: next, action })),
});

/**
 * Define a Redux action's side-effects given an existing `Definition`, there
 * must be at least one effect...
 *
 * ```ts
 * export const { TYPE, create, reduce } = pipe(
 *   type("Missiles fired" as const),
 *   creator((count: number) => ({ count })),
 *   reducer(
 *     ({ state, action: { count } }) =>
 *       (state.message = `${count} missiles incoming!`)
 *   ),
 *   effects(
 *     ({ state: { message } }) => alertAllOfHawaii(message)),
 *     ({ dispatch, action: { count } }) => {
 *       fireMissiles(count);
 *       dispatch(Action.MissilesLanded.action());
 *     })
 *   )
 * )
 * ```
 *
 * The return type of `action` is `Redux.Thunk` action since we're running
 * side-effects.
 */
export const effects = <Config extends Config.Config>(
  ...effects: Effects<Config>
) =>
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  (definition: Definition<Config>) => ({
    ...definition,
    create: (...args: Config["args"]): Thunk<Config> => (
      dispatch,
      getState
    ) => {
      const action = definition.create(...args);

      // eslint-disable-next-line functional/no-expression-statement
      dispatch(action);

      const state = getState();

      // eslint-disable-next-line functional/no-expression-statement
      effects.forEach((effect) => effect({ dispatch, action, state }));

      return action;
    },
  });
